import type { PageLayoutType } from "@v1/utils/element";
import type {
  ReploElement,
  ReploPartialElement,
} from "schemas/generated/element";

import * as React from "react";

import twMerge from "@replo/design-system/utils/twMerge";
import SwitchWithDescription from "@v1/components/designSystem/switch/SwitchWithDescription";
import { elementTypeToEditorData } from "@v1/components/editor/element";
import { useElementEditorDataContext } from "@v1/contexts/ElementEditorDataContext";

import { ElementEditorLayoutButton } from "./ElementEditorLayoutButton";

/**
 * Helper function to get page current layout.
 */
function getCurrentLayout({
  hideDefaultHeader,
  hideDefaultFooter,
}: ReploElement | ReploPartialElement): PageLayoutType {
  if (hideDefaultHeader && hideDefaultFooter) {
    return "no-header-footer";
  }
  if (hideDefaultFooter && !hideDefaultHeader) {
    return "no-footer";
  }
  if (!hideDefaultFooter && hideDefaultHeader) {
    return "no-header";
  }
  return "default";
}

export const ShopifyThemeSettings: React.FC<{
  className?: string;
}> = ({ className }) => {
  const { element, onChangeElement } = useElementEditorDataContext();
  const layout = getCurrentLayout(element);

  return (
    <div className={twMerge("flex flex-col gap-y-4", className)}>
      <SwitchWithDescription
        label="Show Announcement Bar"
        description={`Shows Shopify announcement bar on this ${elementTypeToEditorData[element.type].singularDisplayName.toLowerCase()}.`}
        size="sm"
        variant="card"
        isOn={!element.hideShopifyAnnouncementBar}
        onChange={(shouldShow) => {
          onChangeElement({
            ...element,
            hideShopifyAnnouncementBar: !shouldShow,
          });
        }}
      />
      {element.type === "shopifyProductTemplate" && (
        <SwitchWithDescription
          label="Include Product Schema"
          description="Structured product data will be added to enhance search results."
          size="sm"
          isOn={element.includeProductSchema ?? false}
          onChange={(includeProductSchema) => {
            onChangeElement({ ...element, includeProductSchema });
          }}
          variant="card"
        />
      )}
      <ul className="text-muted grid grid-cols-2 gap-2">
        <li>
          <ElementEditorLayoutButton
            label="Hide Header/Footer"
            isActive={layout === "no-header-footer"}
            onClick={() => {
              return onChangeElement({
                ...element,
                hideDefaultHeader: true,
                hideDefaultFooter: true,
              });
            }}
          />
        </li>
        <li>
          <ElementEditorLayoutButton
            label="Show Header Only"
            isActive={layout === "no-footer"}
            onClick={() => {
              return onChangeElement({
                ...element,
                hideDefaultHeader: false,
                hideDefaultFooter: true,
              });
            }}
          >
            <span className="bg-border h-[15%] w-full rounded-sm" />
          </ElementEditorLayoutButton>
        </li>
        <li>
          <ElementEditorLayoutButton
            label="Show Footer Only"
            isActive={layout === "no-header"}
            onClick={() => {
              return onChangeElement({
                ...element,
                hideDefaultHeader: true,
                hideDefaultFooter: false,
              });
            }}
            className="flex-col-reverse"
          >
            <span className="bg-border h-[15%] w-full rounded-sm" />
          </ElementEditorLayoutButton>
        </li>
        <li>
          <ElementEditorLayoutButton
            label="Show Header/Footer"
            isActive={layout === "default"}
            onClick={() => {
              return onChangeElement({
                ...element,
                hideDefaultHeader: false,
                hideDefaultFooter: false,
              });
            }}
          >
            <span className="bg-border h-[15%] w-full rounded-sm" />
            <span className="bg-border h-[15%] w-full rounded-sm" />
          </ElementEditorLayoutButton>
        </li>
      </ul>
    </div>
  );
};
